import { Map as MapLibreMap } from '@platform/maplibre';
import { MetadataTypes, ProjectTypes } from '@platform/types';
import * as turf from '@turf/turf';
import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import { LngLatBoundsLike, Map, StyleSpecification } from 'maplibre-gl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as peApi from '../../../pe.api';
import AnalysisPanelWorkingLayerSelectionMap from './AnalysisPanelWorkingLayerSelectionMap';

interface Props {
  analysis: ProjectTypes.MapAnalysis;
  workingLayers: ProjectTypes.AnyWorkingLayer[];
  onSelect: (selection: Record<string, ProjectTypes.WorkingLayerFeatureSelection[]>) => void;
}

const AnalysisPanelSelectionMainMap: React.FC<Props> = ({ analysis, workingLayers, onSelect }) => {
  const [selectionLiveGeography, setLiveSelectionGeography] = useState<null | Feature<Polygon | MultiPolygon>>(null);

  const mapRef = useRef<Map>();
  const layerId = 'selection-layer';
  const sourceId = 'selection';

  const [availableDataSourcesPerLayer, setAvailableDataSourcesPerLayer] = useState<
    Record<string, MetadataTypes.MetadataSocialExplorerDataSourceLayer[]>
  >({});

  useEffect(() => {
    const load = async () => {
      try {
        const dataSources = workingLayers
          .filter((wLayer) => wLayer.source === 'social-explorer')
          .map((wLayer) =>
            peApi.getDataSourceLayers((wLayer as ProjectTypes.SocialExplorerWorkingLayer).metadata.surveyName, false)
          );

        const responses = await Promise.all(dataSources);

        const dataSourcesPerLayers = workingLayers.reduce(
          (prev: Record<string, MetadataTypes.MetadataSocialExplorerDataSourceLayer[]>, wLayer, currentIndex) => ({
            ...prev,
            [wLayer.id]: responses[currentIndex],
          }),
          {}
        );
        setAvailableDataSourcesPerLayer(dataSourcesPerLayers);
      } catch {
        // TODO:
        // notify
      }
    };
    load();
  }, [workingLayers]);

  const [featureSelectionPerWorkingLayer, setFeatureSelectionPerWorkingLayer] = useState<
    Record<string, ProjectTypes.WorkingLayerFeatureSelection[]>
  >(workingLayers.reduce((prev, curr) => ({ ...prev, [curr.id]: curr.featureSelection }), {}));

  useEffect(() => onSelect(featureSelectionPerWorkingLayer), [featureSelectionPerWorkingLayer]);

  const mapStyleToUse: StyleSpecification = useMemo(() => {
    const { datasetId, rowIdentifier, nameIdentifier, geoLayerId } = analysis.layer;

    return {
      zoom: 1,
      layers: [
        {
          id: 'all',
          source: sourceId,
          'source-layer': geoLayerId,
          type: 'line',
          paint: {
            'line-color': 'rgb(125,125,125)',
            'line-opacity': 0.5,
            'line-width': 1,
          },
        },
        {
          id: layerId,
          source: sourceId,
          'source-layer': geoLayerId,
          type: 'fill',
          filter: ['in', rowIdentifier, ...analysis.selection.map((f) => f.id)],
          paint: {
            'fill-color': 'red',
            'fill-opacity': 0.5,
          },
        },
      ],
      sources: {
        [sourceId]: {
          type: 'vector',
          tiles: [
            `${process.env.NX_SOCEX_TILES_SERVER}?x={x}&y={y}&z={z}&layers=${geoLayerId}&columns=${geoLayerId}.${datasetId}.${rowIdentifier},${geoLayerId}.${datasetId}.${nameIdentifier}&projection=EPSG-3857`,
          ],
        },
      },
      version: 8,
    };
  }, [analysis]);

  const handleMapIdle = (map: Map) => {
    const selectedFeatures = map.queryRenderedFeatures(undefined, {
      layers: [layerId],
    });
    // @ts-ignore
    const featuresAsTurfPolygons = selectedFeatures.map((f) => turf.multiPolygon(f.geometry.coordinates));
    // @ts-ignore
    const union = featuresAsTurfPolygons.reduce((a, b) => turf.union(a, b), featuresAsTurfPolygons[0]);

    setLiveSelectionGeography(union);
  };

  const handleMapMount = (map: Map) => {
    mapRef.current = map;

    // make sure the map has fitted to bounds of selection layer
    map.once('idle', () => {
      const sourceFeatures =
        map.querySourceFeatures(sourceId, {
          sourceLayer: analysis.layer.geoLayerId,
        }) ?? [];

      // TODO: could be offloaded to ba background worker
      // TODO: can this be optimized further? is turf doing it quick?
      const boundingBox = turf.bbox({
        type: 'FeatureCollection',
        features: sourceFeatures.map((feature) => {
          return {
            type: 'Feature',
            geometry: feature.geometry,
          };
        }),
      }) as LngLatBoundsLike;

      map.fitBounds(boundingBox, { padding: 20, animate: false });
    });
  };

  const handleLayerFeatureSelection = (wId: string, selection: ProjectTypes.WorkingLayerFeatureSelection[]) =>
    setFeatureSelectionPerWorkingLayer((p) => {
      return { ...p, [wId]: selection };
    });

  return (
    <div className="pointer-events-none invisible fixed bottom-1 right-1  z-50 origin-bottom-right scale-[0.1] border-2 bg-white/10">
      <div className="h-[2000px] w-[2000px]">
        <MapLibreMap style={mapStyleToUse} onMount={handleMapMount} onIdle={handleMapIdle} zoom={1} center={[0, 0]} />
      </div>
      <div className="flex gap-1">
        {availableDataSourcesPerLayer &&
          workingLayers
            .filter((x) => x.source === 'social-explorer')
            .map((x) => {
              const dataSourceLayers = availableDataSourcesPerLayer[x.id];

              const dataSourceLayer = getUsedDataSourceLevel(
                dataSourceLayers,
                analysis?.layer.summaryLevel,
                x.metadata.geoLayerId
              );

              if (!dataSourceLayer) return null;

              return (
                <AnalysisPanelWorkingLayerSelectionMap
                  key={x.id}
                  liveGeometry={selectionLiveGeography}
                  workingLayer={x as ProjectTypes.SocialExplorerWorkingLayer}
                  onSelect={handleLayerFeatureSelection}
                  dataSourceLayer={dataSourceLayer}
                />
              );
            })}
      </div>
    </div>
  );
};

export default AnalysisPanelSelectionMainMap;

const getUsedDataSourceLevel = (
  dataSourceLayers: MetadataTypes.MetadataSocialExplorerDataSourceLayer[],
  summaryLevelId: string,
  geoLayerId: string
) => {
  if (!dataSourceLayers) return null;

  // TODO: option A (only matching)
  const dataSourceLayer = dataSourceLayers.find((ds) => ds['summary-level'].id === summaryLevelId);

  // TODO: option B (if not matching than what is used on map)
  // const dataSourceLayer =
  //   dataSourceLayers.find((ds) => ds['summary-level'].id === summaryLevelId) ??
  //   dataSourceLayers.find((ds) => ds['geo-layer-id'] === geoLayerId);

  return dataSourceLayer;
};
